import { useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import gsap from 'gsap';
import BrainMap from './BrainMap';
import RickyMorgan from './RickyMorgan';
import { trustNotes } from '../data/content';
import { useMagnetic } from '../hooks/useMagnetic';

export default function Hero() {
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const trustRef = useRef<HTMLDivElement>(null);
  const magnetic = useMagnetic<HTMLAnchorElement>({ strength: 0.2 });

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.2 });

      // Badge fade in
      tl.fromTo(
        badgeRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }
      );

      // Headline words reveal
      const words = headlineRef.current?.querySelectorAll('.word');
      if (words) {
        tl.fromTo(
          words,
          { y: 50, opacity: 0, rotateX: -30 },
          {
            y: 0,
            opacity: 1,
            rotateX: 0,
            duration: 0.6,
            stagger: 0.06,
            ease: 'power3.out',
          },
          '-=0.2'
        );
      }

      // Subtitle fade in
      tl.fromTo(
        subtitleRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
        '-=0.2'
      );

      // CTA fade in
      tl.fromTo(
        ctaRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
        '-=0.2'
      );

      // Trust notes stagger
      const notes = trustRef.current?.querySelectorAll('.trust-note');
      if (notes) {
        tl.fromTo(
          notes,
          { y: 15, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.4,
            stagger: 0.05,
            ease: 'power2.out',
          },
          '-=0.2'
        );
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <section id="top" className="relative pt-12 sm:pt-16 lg:pt-20">
      <div className="section-container grid items-center gap-10 lg:grid-cols-[0.95fr_1.35fr]">
        <div className="max-w-2xl text-center lg:text-left">
          <div
            ref={badgeRef}
            className="hero-copy mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-violet-100 bg-white/76 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-violet-700 shadow-glass lg:mx-0 opacity-0"
          >
            Adaptive intelligence platform
          </div>
          <h1
            ref={headlineRef}
            className="hero-title text-balance text-5xl font-extrabold leading-[0.95] tracking-[-0.06em] text-slate-950 sm:text-6xl lg:text-7xl"
            style={{ perspective: '600px' }}
          >
            <span className="word inline-block opacity-0">AI</span>{' '}
            <span className="word inline-block opacity-0">beyond</span>{' '}
            <span className="word inline-block opacity-0">chat.</span>
            <br />
            <span className="word inline-block opacity-0">Intelligence</span>{' '}
            <span className="word inline-block opacity-0">that</span>{' '}
            <span className="word inline-block opacity-0 gradient-text-animated">connects.</span>
          </h1>
          <p
            ref={subtitleRef}
            className="mt-6 text-pretty text-lg leading-8 text-slate-600 sm:text-xl opacity-0"
          >
            Relate.ai is an experimental adaptive intelligence platform built to understand people,
            systems, and context — bringing memory, communication, workflow, and action into
            alignment.
          </p>
          <div
            ref={ctaRef}
            className="hero-cta mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start opacity-0"
          >
            <a
              ref={magnetic.ref}
              href="#early-access"
              className="brand-button gap-2"
              style={magnetic.style}
              onMouseMove={magnetic.onMouseMove}
              onMouseLeave={magnetic.onMouseLeave}
              onTouchStart={magnetic.onTouchStart}
              onTouchEnd={magnetic.onTouchEnd}
            >
              Join the Early Release <ArrowRight size={18} aria-hidden="true" />
            </a>
            <p className="text-sm font-bold text-slate-500">
              Target Early Release: <span className="text-slate-950">June 19, 2026.</span>
            </p>
          </div>
          <div ref={trustRef} className="mt-8 flex flex-wrap justify-center gap-3 lg:justify-start">
            {trustNotes.map((note) => (
              <span
                key={note}
                className="trust-note inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/65 px-3 py-2 text-xs font-bold text-slate-600 shadow-glass opacity-0"
              >
                <CheckCircle2 size={15} className="text-emerald-500" aria-hidden="true" /> {note}
              </span>
            ))}
          </div>
        </div>
        <div className="relative grid gap-6 lg:grid-cols-[1fr_0.56fr] lg:items-end">
          <BrainMap />
          <div className="lg:-ml-16 lg:pb-5">
            <RickyMorgan />
          </div>
        </div>
      </div>
    </section>
  );
}
